import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
} from "ra-core";
import { Mail, Phone, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type { Contact } from "../types";
import type { ContentAsset } from "./ContentLibrary";
import { buildMailto, primaryEmail } from "./mailto";
import { renderTemplate } from "./templates";
// The write SEQUENCES live in touchActions.ts so their ORDER is testable
// without a browser -- see touchActions.test.ts. This file must not keep a
// second copy of that logic: a test that exercises a duplicate proves the
// duplicate is right and says nothing about the code that runs.
// todayLocal is imported rather than redefined: dateOnlyToTimestamp uses the
// same function to decide whether the chosen date IS today and should keep
// the real clock. Two definitions of "today" is how that check quietly stops
// matching at a timezone boundary.
import {
  draftEmailAndStamp,
  invalidateTouched,
  logTouch,
  todayLocal,
  type ClosableTask,
} from "./touchActions";
// The content RULES live in taskDraft.ts for the same reason: which template a
// draft opens on, and whose words it uses, are decided there and asserted in
// the `marketing` project without a browser.
import {
  draftContent,
  preselectedTemplateId,
  templateOptions,
  type DraftStep,
} from "./taskDraft";
import { contactMergeValues } from "./stepContent";

/**
 * Two actions on a contact: log a touch that already happened, or draft an
 * email through it.
 *
 * WHY THE EMAIL STAMP IS NOT OPTIONAL
 * ------------------------------------
 * `contacts.last_contacted_at` is derived from `engagements` by a single DB
 * trigger (see 050_engagement_triggers.sql) -- it is never written directly,
 * and nothing else keeps it honest. If Draft email only opened the mail
 * client, a lead you personally emailed a minute ago would still show up in
 * `contacts_stalled` as untouched. That is the exact Zoho failure this whole
 * system exists to prevent, reproduced inside a nicer button. So opening the
 * mailto: URL and writing the engagement row happen in the same click --
 * there is no code path where one happens without the other.
 *
 * WHY LOG TOUCH IS BACK-DATEABLE
 * --------------------------------
 * `engagements.occurred_at` records when contact happened, not when it was
 * typed in. Logging Tuesday's call on Thursday must stamp Tuesday, or the
 * cadence clock (also derived from occurred_at) starts lying about how
 * recently someone was actually reached.
 *
 * WHY A DISABLED BUTTON EXPLAINS ITSELF
 * ----------------------------------------
 * A button that quietly does nothing when a contact has no email teaches the
 * person clicking it that the app is broken. The Draft email button is
 * disabled with the reason on display instead -- both as a native title
 * tooltip and as inline text, so it is not something you can only discover
 * by hovering.
 */

/**
 * What these dialogs need so a phone can reach all of one.
 *
 * TWO SEPARATE THINGS, both measured at 320x700 against a twelve-paragraph
 * template -- which is an ordinary length for an email, not a stress case.
 *
 * 1. NO HEIGHT CAP AND NO OVERFLOW. `ui/dialog.tsx` centers DialogContent with
 *    `fixed top-[50%] translate-y-[-50%]` and caps its WIDTH with
 *    `max-w-[calc(100%-2rem)]`, with no matching height rule. So a dialog
 *    taller than the viewport is centered ON the viewport and hangs off BOTH
 *    ends: the Draft email dialog measured top -346.41 and bottom 1046.41 in a
 *    700px window, `overflow-y: visible`, no scrollbar anywhere -- because the
 *    thing that overflowed is not the page behind it. The title, the template
 *    picker, the preview and "Open email draft" were all unreachable, and the
 *    only way out was Escape. `max-h-[calc(100%-2rem)]` MIRRORS the width rule
 *    on purpose: a percentage on a `fixed` element resolves against the
 *    viewport, so the two axes stay in step by looking like each other. With
 *    it: 17 to 683, and 1391px of content scrolling inside 664.
 *
 * 2. AND THE WIDTH CAP WAS BEING CANCELLED BY THIS FILE. Upstream's
 *    `max-w-[calc(100%-2rem)]` is the rule that keeps a dialog off the edges
 *    of a phone -- and the `max-w-md` / `max-w-lg` these two passed to `cn()`
 *    are in the same tailwind-merge group, so each one DELETED it. The dialog
 *    rendered 0.00 to 320.00, flush against both edges. Scoping our own cap
 *    to `sm:` is the whole fix: upstream's unmodified rule then survives and
 *    owns the phone (17 to 303 at 320px, the same column MobileContent gives
 *    the page behind it) while ours owns every width above 640. The same
 *    tailwind-merge trap c2c12ea4 hit with `has-[>svg]:px-3`, in the other
 *    direction -- there a plain class could not cancel a modified one; here
 *    an unmodified one canceled the wrong thing.
 *
 *    SO THIS CONSTANT DOES NOT RESTATE `max-w-[calc(100%-2rem)]`. It did at
 *    first; deleting it was GREEN against the whole suite, because with the
 *    `sm:` scoping there is nothing left canceling upstream's copy. Kept, it
 *    would read as the thing holding the phone inset up while the `sm:` did
 *    the work -- and the next person to simplify would take the wrong one.
 *
 *    ONE DESKTOP NUMBER MOVES, measured at 1280: Log touch was 512px wide and
 *    is now 448. Its `max-w-md` had been overridden at every width above 640
 *    by upstream's `sm:max-w-lg`, which tailwind-merge leaves alone because
 *    the modifier differs -- so that dialog has never been the width it asks
 *    to be, and scoping ours to `sm:` is what finally applies it. Draft email
 *    is 512 before and after; its cap and upstream's agree.
 *
 * NOT FIXED IN `ui/dialog.tsx`, which is upstream's and is imported by every
 * other dialog in the app: this fork merges from marmelab constantly, so a
 * change there is merge surface plus a behavior change to screens nobody has
 * measured. Local to the two dialogs shown to need it.
 */
const PHONE_SAFE_DIALOG = "max-h-[calc(100%-2rem)] overflow-y-auto";

const TOUCH_KINDS = [
  { id: "call", name: "Call" },
  { id: "email", name: "Email" },
  { id: "met", name: "Met" },
  { id: "other", name: "Other" },
] as const;

type TouchKind = (typeof TOUCH_KINDS)[number]["id"];

export const TouchButtons = () => {
  const contact = useRecordContext<Contact>();
  const [logOpen, setLogOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);

  if (!contact) return null;

  const email = primaryEmail(contact);

  return (
    /*
      `data-slot` because this row is rendered on BOTH contact layouts and its
      geometry is asserted on the narrow one -- contactActionsMobile.test.tsx
      measures both ends of every child at 320px. Reaching it through
      `getByRole("button").element().parentElement` would be a positional pick
      that silently starts describing a different element the moment a wrapper
      is added. `data-slot` is how every shadcn primitive in this app is
      addressed, so this is the house selector rather than a test-only hook.

      `flex-wrap` is load-bearing at 320px rather than decoration, and that is
      measured: with the no-address explanation beside them the row's content
      is wider than the phone, and on one line the sentence ends at 323.33 in
      a 320px window. Wrapping is what keeps both ends of every item on
      screen -- deleting it fails contactActionsMobile.test.tsx on that
      number.
    */
    <div
      data-slot="touch-actions"
      className="flex flex-wrap items-center gap-2 mt-4"
    >
      <Button variant="outline" size="sm" onClick={() => setLogOpen(true)}>
        <Phone className="size-4" /> Log touch
      </Button>

      <Button
        variant="outline"
        size="sm"
        disabled={!email}
        title={email ? undefined : "This contact has no email address on file"}
        onClick={() => setDraftOpen(true)}
      >
        <Mail className="size-4" /> Draft email
      </Button>
      {/*
        A BLOCKED ACTION LINKS TO ITS BLOCKER. House rule, applied here first.

        Saying "No email address on file" beside a dead button describes the
        obstacle and leaves the reader to work out where it is fixed. The
        reason someone is looking at this button is that they wanted to email
        this person, so the sentence should end somewhere they can act.
      */}
      {!email && (
        <span className="text-xs text-muted-foreground">
          No email address on file —{" "}
          <Link
            to={`/contacts/${contact.id}`}
            className="underline hover:no-underline"
          >
            add one
          </Link>
        </span>
      )}

      <LogTouchDialog
        contact={contact}
        open={logOpen}
        onOpenChange={setLogOpen}
      />
      {email && (
        <DraftEmailDialog
          contact={contact}
          email={email}
          open={draftOpen}
          onOpenChange={setDraftOpen}
        />
      )}
    </div>
  );
};

const LogTouchDialog = ({
  contact,
  open,
  onOpenChange,
}: {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  /**
   * Refetch what the write changed.
   *
   * `useDataProvider()` writes straight past react-admin's mutation hooks, so
   * nothing invalidates the query cache. Reported symptom: the toast says
   * "Touch logged", the row is in the database, and the touch history on the
   * same screen does not move until the page is reloaded — which teaches you
   * the panel is unreliable, about the one screen whose entire job is being
   * believed.
   */
  const queryClient = useQueryClient();
  const refetchTouched = () => invalidateTouched(queryClient);

  const [kind, setKind] = useState<TouchKind>("call");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(todayLocal());
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setKind("call");
    setNote("");
    setDate(todayLocal());
  };

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await logTouch(dataProvider, {
        contactId: contact.id,
        kind,
        note,
        date,
      });
      await refetchTouched();
      notify("Touch logged.", { type: "success" });
      onOpenChange(false);
      reset();
    } catch (e) {
      notify((e as Error).message ?? "Failed to log the touch", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className={`sm:max-w-md ${PHONE_SAFE_DIALOG}`}>
        <DialogHeader>
          <DialogTitle>Log a touch</DialogTitle>
          <DialogDescription>
            Record contact that already happened. The date is back-dateable --
            logging Tuesday's call on Thursday should say Tuesday.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as TouchKind)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOUCH_KINDS.map((k) => (
                  <SelectItem key={k.id} value={k.id}>
                    {k.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="touch-date">Date</Label>
            <Input
              id="touch-date"
              type="date"
              value={date}
              max={todayLocal()}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="touch-note">Note (optional)</Label>
            <Textarea
              id="touch-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What happened…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={busy || !date}>
            {busy ? "Logging…" : "Log touch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The Draft email dialog, used by the contact page and by a task's own menu.
 *
 * EXPORTED SO THERE IS EXACTLY ONE OF IT. `TaskDraftEmail.tsx` opens this same
 * component with a step and a task attached; a second copy would be a second
 * place for the content rule and the stamp ORDER to drift, and that order is
 * the load-bearing thing in the whole feature.
 *
 * The three optional props ARE the difference between the two entry points,
 * and all three are absent from the contact page on purpose:
 *
 *   step / stepAsset  which template to open on, resolved through
 *                     `enrollment_steps.content_id`. Absent -> nothing is
 *                     preselected and a choice is required (crm#16).
 *   task              the one task this draft may close. Absent -> it closes
 *                     nothing at all (crm#13).
 */
export const DraftEmailDialog = ({
  contact,
  email,
  open,
  onOpenChange,
  step,
  stepAsset,
  task,
}: {
  contact: Contact;
  email: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The enrollment step this draft came from, when it came from one. */
  step?: DraftStep | null;
  /** That step's asset, so a retired one is still offered and preselected. */
  stepAsset?: ContentAsset | null;
  /** The task this draft came from. The ONLY task it may close. */
  task?: ClosableTask | null;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  /**
   * Refetch what the write changed.
   *
   * `useDataProvider()` writes straight past react-admin's mutation hooks, so
   * nothing invalidates the query cache. Reported symptom: the toast says
   * "Touch logged", the row is in the database, and the touch history on the
   * same screen does not move until the page is reloaded — which teaches you
   * the panel is unreliable, about the one screen whose entire job is being
   * believed.
   */
  const queryClient = useQueryClient();
  const refetchTouched = () => invalidateTouched(queryClient);

  /**
   * `null` MEANS "NOBODY HAS TOUCHED THE PICKER YET", not "nothing chosen".
   *
   * It used to be a "" that an effect filled in with `templates[0]` -- the
   * alphabetically first active asset -- the moment the list arrived, and
   * `draftEmailAndStamp` then wrote that id onto the engagement as
   * `content_id`. So the app recorded an asset the user never picked (crm#16).
   *
   * The selection is DERIVED rather than latched, because the step resolves
   * before the asset it names: an effect would run once against options that
   * did not contain it yet and settle on nothing. A person's pick still wins
   * for as long as the dialog is open, which is what `null` distinguishes.
   */
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: templates, isPending } = useGetList<ContentAsset>(
    "content_assets",
    {
      filter: { kind: "email", is_active: true },
      sort: { field: "name", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: open },
  );

  const options = templateOptions(templates, stepAsset);
  const templateId = picked ?? preselectedTemplateId(step, options);
  const template = options.find((t) => String(t.id) === templateId);

  const values = contactMergeValues(contact);

  // The step's own override beats the library row, and only for the step's own
  // asset -- see draftContent. Null while nothing is chosen.
  const content = draftContent(step, template);
  const renderedSubject = content
    ? renderTemplate(content.subject ?? "", values)
    : "";
  const renderedBody = content ? renderTemplate(content.body, values) : "";

  /**
   * CLOSING GOES THROUGH ONE FUNCTION, and the first version did not.
   *
   * `Dialog`'s own `onOpenChange` fires for Escape and the overlay, but the
   * Cancel button and the success path called `onOpenChange(false)` directly
   * and skipped the reset -- so the picker kept the last draft's template and
   * showed it, unchosen, on the next one. Caught by
   * contactActionsMobile.test.tsx's reopen assertion, which is why that test
   * is not decoration.
   */
  const close = () => {
    setPicked(null);
    onOpenChange(false);
  };

  const handleOpenDraft = async () => {
    if (!template) return;
    const url = buildMailto(email, renderedSubject, renderedBody);
    if (!url) {
      notify("This contact has no email address on file", { type: "error" });
      return;
    }
    setBusy(true);
    try {
      // The stamp happens BEFORE the handoff, and the handoff is injected so
      // that order is observable to a test. See touchActions.ts.
      await draftEmailAndStamp(dataProvider, {
        contactId: contact.id,
        templateId: template.id,
        templateName: template.name,
        url,
        openUrl: (target) => {
          window.location.href = target;
        },
        // Undefined from the contact page, and that is the whole of crm#13:
        // with no task in the click there is nothing truthful to close.
        task,
      });
      await refetchTouched();
      notify("Draft opened and logged.", { type: "success" });
      close();
    } catch (e) {
      notify((e as Error).message ?? "Failed to log the email", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Forget the PICK, never the preselect: reopening from the same task
        // must land on that step's content again rather than on whatever was
        // chosen last time.
        if (o) onOpenChange(true);
        else close();
      }}
    >
      <DialogContent className={`sm:max-w-lg ${PHONE_SAFE_DIALOG}`}>
        <DialogHeader>
          <DialogTitle>Draft an email</DialogTitle>
          <DialogDescription>
            {task
              ? "Opens your mail client with this task's own content filled in, logs it as a touch, and closes this task -- no other."
              : "Opens your mail client with the template filled in, and logs it as a touch in the same step."}
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <p className="text-sm text-muted-foreground">Loading templates…</p>
        ) : !options.length ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              There is no active email template yet, so there is nothing to
              send. Templates live in the content library.
            </p>
            {/*
              The same house rule as the disabled button above: name the
              blocker AND go there. "Add one there first" told the reader what
              to do and made them find it -- and the library was, at that
              point, filed under Pipeline, which is not where anyone would
              look for it.
            */}
            <Button asChild variant="outline" size="sm">
              <Link to="/content_assets/create">
                <Plus className="size-4" /> Write the first template
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Template</Label>
              {/*
                A PLACEHOLDER, NOT A PRESELECTED FIRST ROW. With no step there
                is no opinion to represent, and `templates[0]` represented one
                the user had not expressed -- then wrote it onto the engagement
                as the asset that was sent (crm#16). "Open email draft" stays
                disabled until this says something.
              */}
              <Select value={templateId} onValueChange={setPicked}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/*
                WHERE THE PRESELECT CAME FROM, in words. The failure crm#16
                describes is invisible rather than loud -- a wrong template
                looks exactly like a right one -- so the dialog says which
                asset the step asked for, and a person who changes the picker
                can see they have departed from it.
              */}
              {step != null && (
                <p className="text-xs text-muted-foreground">
                  {stepAsset
                    ? templateId === String(stepAsset.id)
                      ? `This task's step asks for "${stepAsset.name}".`
                      : `This task's step asks for "${stepAsset.name}" — you have chosen something else.`
                    : "This task's step names no template, so there is nothing to preselect."}
                </p>
              )}
            </div>

            {template && (
              <div className="text-xs text-muted-foreground border rounded-md p-3 space-y-1.5">
                <p>
                  <span className="font-medium text-foreground">Subject: </span>
                  {renderedSubject || "—"}
                </p>
                <p className="whitespace-pre-wrap">{renderedBody}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleOpenDraft} disabled={busy || !template}>
            {busy ? "Opening…" : "Open email draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
